<template>
  <v-dialog
    v-model="show"
    persistent
    @keydown.esc="hide"
    width="500"
    v-if="role('safetyRepresentative')"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        v-if="role('safetyRepresentative')"
        v-on="on"
        small
        class="primary accent-4 white--text mr-2 my-1"
      >
        {{ $t(`profile.assignment.changeAccounts.title`) }}
        {{ oldAccounts && oldAccounts.length ? oldAccounts.length : "" }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="headline grey lighten-2" primary-title>
        {{ $t(`profile.assignment.changeAccounts.title`) }}
      </v-card-title>
      <v-card-text class="mt-4">
        <span v-if="oldAccountsInternal.length === 0">
          {{ $t(`profile.assignment.changeAccounts.explanation`) }}
        </span>
        <span v-else>
          {{ $t(`profile.assignment.changeAccounts.explanationWithAccounts`) }}
        </span>

        <v-row no-gutters class="my-2">
          <template v-for="(oldAccount, index) in oldAccountsInternal">
            <v-chip
              :key="index"
              class="mr-2 mb-2"
              close
              close-icon="delete_forever"
              @click:close="confirmRemoveAccount(oldAccount, index)"
            >
              <router-link
                :to="{ name: 'employee', params: { id: oldAccount.id } }"
              >
                {{ $options.filters.nameId(oldAccount) }}
              </router-link>
            </v-chip>
          </template>
        </v-row>

        <v-autocomplete
          ref="newOldAccountAutoComplete"
          class="mt-0 pt-0"
          v-model="newOldAccount"
          :items="employeesAll"
          :item-text="(e) => `${$options.filters.nameId(e)}`"
          item-value="id"
          chips
          :rules="[rules.required]"
          :label="$t('profile.assignment.changeAccounts.addAccount')"
        />

        <v-checkbox
          v-model="transferTags"
          :label="$t('profile.assignment.changeAccounts.transferTags')"
        />
        <v-checkbox
          class="mt-0 pt-0"
          v-model="transferInstructions"
          :label="$t('profile.assignment.changeAccounts.transferInstructions')"
        />
        <v-checkbox
          class="mt-0 pt-0"
          v-model="transferEmail"
          :label="$t('profile.assignment.changeAccounts.transferEmail')"
        />
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="grey" text @click="hide">{{ $t("dialog.cancel") }}</v-btn>

        <v-btn
          color="primary"
          text
          :disabled="!newOldAccount"
          @click="addAccount"
          :loading="loading"
        >
          {{ $t("dialog.change") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="confirmDialog" max-width="350px">
      <v-card>
        <v-card-title class="headline">{{
          $t("profile.assignment.changeAccounts.removeTitle")
        }}</v-card-title>
        <v-card-text>
          {{ $t("profile.assignment.changeAccounts.warning") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="confirmDialog = false">{{
            $t("dialog.cancel")
          }}</v-btn>
          <v-btn color="primary" text @click="removeAccount">{{
            $t("profile.assignment.changeAccounts.confirmButton")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import AllEmployeesQuery from "@/graphql/queries/employee/AllEmployees.gql";
import AddOldAccount from "@/graphql/mutation/profile/AddOldAccount.gql";
import RemoveOldAccount from "@/graphql/mutation/profile/RemoveOldAccount.gql";

export default {
  props: ["id", "oldAccounts"],
  data() {
    return {
      loading: false,
      show: false,
      newOldAccount: null,
      oldAccountsInternal: [],

      transferTags: false,
      transferInstructions: false,
      transferEmail: false,

      accountToRemove: null,
      confirmDialog: false,
      removeIndex: null,
      rules: {
        required: (value) => !!value || this.$i18n.t("validation.required"),
      },
    };
  },
  watch: {
    oldAccounts: {
      immediate: true,
      handler(currentAccounts) {
        this.oldAccountsInternal = currentAccounts ? [...currentAccounts] : [];
      },
    },
    $route() {
      this.hide();
    },
  },
  computed: {
    ...mapGetters(["role"]),
  },
  methods: {
    confirmRemoveAccount(account, index) {
      this.accountToRemove = account;
      this.removeIndex = index;
      this.confirmDialog = true;
    },
    removeAccount() {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: RemoveOldAccount,
          variables: {
            employeeId: this.id,
            oldAccountId: this.accountToRemove.id,
          },
        })
        .then(({ data }) => {
          this.oldAccountsInternal.splice(this.removeIndex, 1);
          this.$emit("refresh", data.removeOldAccount);
          this.$store.commit("push_global_message", {
            color: "warning",
            text: `${this.$t(
              "profile.assignment.changeAccounts.accountRemoved"
            )} ${this.$options.filters.nameId(this.accountToRemove)}`,
          });
          this.confirmDialog = false;
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err.message,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addAccount() {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: AddOldAccount,
          variables: {
            employeeId: this.id,
            oldAccountId: this.newOldAccount,
            transferTags: this.transferTags,
            transferInstructions: this.transferInstructions,
            transferEmail: this.transferEmail,
          },
        })
        .then(() => {
          this.$emit("refresh");
          this.oldAccountsInternal.push({
            id: this.newOldAccount,
            name: this.$options.filters.nameId({ id: this.newOldAccount }),
          });

          this.$nextTick(() => {
            this.newOldAccount = null;
            this.transferTags = false;
            this.transferInstructions = false;
            this.transferEmail = false;
            this.$refs.newOldAccountAutoComplete.reset();
          });
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err.message,
          });
        })
        .finally(() => {
          this.loading = false;
          this.hide();
        });
    },
    hide() {
      this.show = false;
      this.transferTags = false;
      this.transferInstructions = false;
      this.transferEmail = false;
      this.newOldAccount = null;
      this.loading = false;
      this.accountToRemove = null;
      this.confirmDialog = false;
      this.removeIndex = null;
      if (this.$refs.newOldAccountAutoComplete) {
        this.$refs.newOldAccountAutoComplete.reset();
      }
    },
  },
  apollo: {
    employeesAll: {
      query: AllEmployeesQuery,
      skip() {
        return !this.show;
      },
      update(data) {
        const oldAccountIds = this.oldAccounts
          ? this.oldAccounts.map((account) => account.id)
          : [];
        return data.employeesAll.filter(
          (e) => e.id != this.id && !oldAccountIds.includes(e.id)
        );
      },
    },
  },
};
</script>
